import { StaffColor } from "../lib/staff-colors.ts";
import type { FormatPrefs } from "../lib/i18n.ts";
import { formatTime as fmtTimeI18n, formatDate as fmtDateI18n } from "../lib/i18n.ts";
import { decodeEntities } from "../lib/decode-entities.ts";

interface Appointment {
  id: string;
  start_at: string;
  end_at: string;
  status: string;
  customer_name: string;
  customer_email?: string | null;
  customer_phone?: string | null;
  service_name: string;
  staff_id: string;
  staff_name: string;
}

interface Props {
  appointments: Appointment[];
  /** ISO YYYY-MM-DD date range, inclusive. */
  fromISO: string;
  toISO: string;
  staffColorMap: Map<string, StaffColor>;
  onSelect?: (id: string) => void;
  formatPrefs?: FormatPrefs;
}

function fmtTime(iso: string, prefs?: FormatPrefs): string {
  if (prefs) return fmtTimeI18n(iso, prefs);
  return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function ymdLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

function dayLabel(iso: string, prefs?: FormatPrefs): string {
  const d = new Date(`${iso}T12:00:00`);
  const today = ymdLocal(new Date());
  const tmrw = (() => {
    const t = new Date();
    t.setDate(t.getDate() + 1);
    return ymdLocal(t);
  })();
  const baseOpts = { weekday: "long", month: "long", day: "numeric" } as const;
  const fmt = (opts: Intl.DateTimeFormatOptions) =>
    prefs ? fmtDateI18n(d, prefs, opts) : d.toLocaleDateString(undefined, opts);
  if (iso === today) return `Today · ${fmt(baseOpts)}`;
  if (iso === tmrw) return `Tomorrow · ${fmt(baseOpts)}`;
  return fmt({ ...baseOpts, year: "numeric" });
}

function statusPill(status: string): { bg: string; text: string; label: string } {
  const map: Record<string, { bg: string; text: string; label: string }> = {
    confirmed: { bg: "var(--status-confirmed-bg)", text: "var(--status-confirmed)", label: "Confirmed" },
    completed: { bg: "var(--status-done-bg)", text: "var(--status-done)", label: "Complete" },
    no_show: { bg: "var(--status-noshow-bg)", text: "var(--status-noshow)", label: "No-show" },
    cancelled: { bg: "var(--status-cancelled-bg)", text: "var(--status-cancelled)", label: "Cancelled" },
  };
  return map[status] ?? { bg: "var(--paper-well)", text: "var(--ink-soft)", label: status };
}

export function AgendaView({ appointments, fromISO, toISO, staffColorMap, onSelect, formatPrefs }: Props) {
  // Filter to range, sort, group by day.
  const sorted = [...appointments]
    .filter((a) => {
      const day = a.start_at.split("T")[0];
      return day >= fromISO && day <= toISO;
    })
    .sort((a, b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime());

  const groups = new Map<string, Appointment[]>();
  for (const a of sorted) {
    const day = a.start_at.split("T")[0];
    const list = groups.get(day) ?? [];
    list.push(a);
    groups.set(day, list);
  }

  // Build the day list including empty days in the range.
  const days: string[] = [];
  {
    const start = new Date(`${fromISO}T12:00:00`);
    const end = new Date(`${toISO}T12:00:00`);
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      days.push(ymdLocal(d));
    }
  }

  if (sorted.length === 0) {
    return (
      <div
        style={{
          padding: "3rem 1.5rem",
          textAlign: "center",
          color: "var(--text-muted)",
          fontSize: "0.9375rem",
          background: "var(--paper-raised)",
          borderRadius: 14,
          boxShadow: "var(--shadow-sm)",
        }}
      >
        No appointments in this range.
      </div>
    );
  }

  return (
    <div
      style={{
        background: "var(--paper-raised)",
        borderRadius: 14,
        boxShadow: "var(--shadow-sm)",
        overflow: "hidden",
      }}
    >
      {days.map((day) => {
        const dayAppts = groups.get(day) ?? [];
        return (
          <section key={day}>
            <header
              style={{
                padding: "12px 16px 8px",
                background: "var(--bg-secondary)",
                borderBottom: "1px solid var(--rule)",
                fontFamily: "'Source Serif 4', Georgia, serif",
                fontSize: 14,
                fontWeight: 500,
                color: dayAppts.length === 0 ? "var(--ink-soft)" : "var(--ink)",
                position: "sticky",
                top: 0,
                zIndex: 1,
              }}
            >
              {dayLabel(day, formatPrefs)}
              <span style={{ marginLeft: 8, color: "var(--ink-faint)", fontSize: 12, fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>
                {dayAppts.length === 0
                  ? "No appointments"
                  : `${dayAppts.length} ${dayAppts.length === 1 ? "appointment" : "appointments"}`}
              </span>
            </header>
            {dayAppts.length === 0 && (
              <div
                style={{
                  padding: "14px 16px",
                  fontSize: 13,
                  color: "var(--ink-faint)",
                  fontStyle: "italic",
                  borderBottom: "1px solid var(--rule)",
                }}
              >
                Open day — nothing on the schedule.
              </div>
            )}
            {dayAppts.length > 0 && (
            <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {dayAppts.map((appt) => {
                const color = staffColorMap.get(appt.staff_id);
                const status = statusPill(appt.status);
                const isCompleted = appt.status === "completed";
                const isCancelled = appt.status === "cancelled";
                const isNoShow = appt.status === "no_show";
                return (
                  <li
                    key={appt.id}
                    onClick={onSelect ? () => onSelect(appt.id) : undefined}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "92px 1fr auto",
                      alignItems: "center",
                      gap: 12,
                      padding: "12px 16px",
                      borderBottom: "1px solid var(--rule)",
                      cursor: onSelect ? "pointer" : "default",
                      // Soft dim for past states; still legible.
                      opacity: isCompleted ? 0.7 : isCancelled ? 0.55 : 1,
                      textDecoration: isNoShow ? "line-through" : undefined,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "var(--paper-well)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "transparent";
                    }}
                  >
                    <div
                      style={{
                        fontSize: 13,
                        fontVariantNumeric: "tabular-nums",
                        color: "var(--ink-soft)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {fmtTime(appt.start_at, formatPrefs)}–{fmtTime(appt.end_at, formatPrefs)}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 14,
                          fontWeight: 600,
                          color: "var(--ink)",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {decodeEntities(appt.customer_name)}
                      </div>
                      <div
                        style={{
                          fontSize: 13,
                          color: "var(--ink-soft)",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          marginTop: 2,
                        }}
                      >
                        {decodeEntities(appt.service_name)}
                        <span style={{ color: "var(--ink-faint)", margin: "0 6px" }}>·</span>
                        <span style={{ color: color?.text ?? "var(--ink-soft)", fontWeight: 500 }}>
                          {appt.staff_name}
                        </span>
                      </div>
                    </div>
                    <span
                      style={{
                        fontSize: 9.5,
                        fontWeight: 600,
                        padding: "2px 8px",
                        borderRadius: 999,
                        background: status.bg,
                        color: status.text,
                        letterSpacing: "0.04em",
                        textTransform: "uppercase",
                      }}
                    >
                      {status.label}
                    </span>
                  </li>
                );
              })}
            </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}
